import React, {createContext, useContext, ReactNode, useMemo, useCallback} from 'react';
import {AlertColor, IconButton} from '@mui/material';
import {SnackbarProvider, useSnackbar, VariantType} from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

interface NotificationContextProps {
    showNotification: (message: string, severity?: AlertColor) => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

const MAX_SNACKBAR = 3;
const AUTO_HIDE_DURATION = 6000;

export const NotificationProvider = ({children}: { children: ReactNode }) => {
    return (
        <SnackbarProvider maxSnack={MAX_SNACKBAR} anchorOrigin={{vertical: 'top', horizontal: 'right'}}>
            <NotificationContextProvider>{children}</NotificationContextProvider>
        </SnackbarProvider>
    );
};

export const useNotification = (): NotificationContextProps => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

const NotificationContextProvider = ({children}: { children: ReactNode }) => {
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();

    const showNotification = useCallback((message: string, severity: AlertColor = 'info') => {
            const variant: VariantType = severity;
            enqueueSnackbar(message, {
                variant,
                autoHideDuration: AUTO_HIDE_DURATION,
                action: (key) => <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => closeSnackbar(key)}
                >
                    <CloseIcon fontSize="inherit"/>
                </IconButton>
            });
        },
        [enqueueSnackbar, closeSnackbar]
    );

    const value = useMemo(() => ({showNotification}), [showNotification]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

